import React from 'react';
export type BadgeStatus = 'processing' | 'complete' | 'failed' | 'pending' | 'info';
export type BadgeSize = 'sm' | 'md';
export interface StatusBadgeProps {
  status: BadgeStatus;
  size?: BadgeSize;
  children: React.ReactNode;
  className?: string;
  dot?: boolean;
}
const statusStyles: Record<BadgeStatus, {
  bg: string;
  text: string;
  dot: string;
}> = {
  processing: {
    bg: 'bg-primary-50',
    text: 'text-primary-700',
    dot: 'bg-primary-500'
  },
  complete: {
    bg: 'bg-success-50',
    text: 'text-success-700',
    dot: 'bg-success-500'
  },
  failed: {
    bg: 'bg-error-50',
    text: 'text-error-700',
    dot: 'bg-error-500'
  },
  pending: {
    bg: 'bg-warning-50',
    text: 'text-warning-700',
    dot: 'bg-warning-500'
  },
  info: {
    bg: 'bg-surface-100',
    text: 'text-surface-700',
    dot: 'bg-surface-500'
  }
};
const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-caption',
  md: 'px-2.5 py-1 text-body-sm'
};
export function StatusBadge({
  status,
  size = 'md',
  children,
  className = '',
  dot = true
}: StatusBadgeProps) {
  const styles = statusStyles[status];
  return <span className={`
        inline-flex items-center gap-1.5
        font-medium rounded-badge
        ${styles.bg} ${styles.text}
        ${sizeStyles[size]}
        ${className}
      `} data-id="element-541">
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${styles.dot} ${status === 'processing' ? 'animate-pulse' : ''}`} aria-hidden="true" data-id="element-542" />}
      {children}
    </span>;
}
// Convenience components for common statuses
export function ProcessingBadge({
  children = 'Processing',
  ...props
}: Omit<StatusBadgeProps, 'status'>) {
  return <StatusBadge status="processing" {...props} data-id="element-543">
      {children}
    </StatusBadge>;
}
export function CompleteBadge({
  children = 'Complete',
  ...props
}: Omit<StatusBadgeProps, 'status'>) {
  return <StatusBadge status="complete" {...props} data-id="element-544">
      {children}
    </StatusBadge>;
}
export function FailedBadge({
  children = 'Failed',
  ...props
}: Omit<StatusBadgeProps, 'status'>) {
  return <StatusBadge status="failed" {...props} data-id="element-545">
      {children}
    </StatusBadge>;
}
export function PendingBadge({
  children = 'Pending',
  ...props
}: Omit<StatusBadgeProps, 'status'>) {
  return <StatusBadge status="pending" {...props} data-id="element-546">
      {children}
    </StatusBadge>;
}